import React, { useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Paper
} from '@mui/material';
import {
  PlaylistAddCheck as ListCleanerIcon,
  Search as ResearchIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

/**
 * HomePage - Landing dashboard for authenticated users
 *
 * Features:
 * - Two-row grid layout with Wisp centered at top
 * - Two feature cards centered: Clean a List, Analyze Websites
 * - Fully responsive layout
 */
export default function HomePage({ onNavigate }) {
  const videoRef = useRef(null);

  useEffect(() => {
    // Play video once when component mounts
    if (videoRef.current) {
      videoRef.current.play().catch(err => {
        console.log('Video autoplay prevented:', err);
      });
    }
  }, []);

  const handleNavigate = (mode) => {
    if (onNavigate) {
      onNavigate(mode);
    }
  };

  return (
    <Container maxWidth={false} sx={{ py: 4 }}>
      <Stack direction="column" spacing={4}>

        {/* Top Row - 3 Invisible Guide Boxes with Wisp in Center */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',                    // 1 column on mobile
              md: 'repeat(3, 1fr)'          // 3 equal columns on desktop
            },
            gap: 3,
            maxWidth: '1600px',
            margin: '0 auto',
            width: '100%',
            px: { xs: 2, md: 0 },
            minHeight: '280px'
          }}
        >
          {/* Empty Box 1 */}
          <Box sx={{ backgroundColor: 'transparent' }} />

          {/* Center Box - Wisp + Speech Bubble */}
          <Box
            sx={{
              backgroundColor: 'transparent',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2
            }}
          >
            {/* Speech Bubble */}
            <Paper
              elevation={3}
              sx={{
                position: 'relative',
                p: 2.5,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #161c3e 0%, #0D0D21 100%)',
                maxWidth: '320px',
                width: '100%',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  right: '50%',
                  bottom: '-15px',
                  transform: 'translateX(50%)',
                  width: 0,
                  height: 0,
                  borderStyle: 'solid',
                  borderWidth: '15px 15px 0 15px',
                  borderColor: '#161c3e transparent transparent transparent'
                }
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: '#FFFFFF',
                  fontWeight: 700,
                  mb: 1,
                  textAlign: 'center',
                  fontSize: '1.1rem'
                }}
              >
                I'm Wisp!
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#B0B0B0',
                  textAlign: 'center',
                  lineHeight: 1.5,
                  fontSize: '0.875rem'
                }}
              >
                How can I help you with your sales today?
              </Typography>
            </Paper>

            {/* Wisp Animation */}
            <Box
              sx={{
                maxWidth: '150px',
                width: '100%',
                mt: 1
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                loop
                muted
                playsInline
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }}
              >
                <source src="/looped_seamless.webm" type="video/webm" />
                Your browser does not support the video tag.
              </video>
            </Box>
          </Box>

          {/* Empty Box 3 */}
          <Box sx={{ backgroundColor: 'transparent' }} />
        </Box>

        {/* Bottom Row - Four Feature Cards - Using CSS Grid with Custom Sizing */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',                    // 1 column on mobile
              md: '0.5fr 1fr 1fr 0.5fr'     // Custom: boxes 1&4 half size, boxes 2&3 full size
            },
            gap: 3,
            maxWidth: '1600px',
            margin: '0 auto',
            width: '100%',
            px: { xs: 2, md: 0 }
          }}
        >
          {/* Empty Box 1 - Background Matching */}
          <Box sx={{ backgroundColor: 'transparent' }} />

          {/* Clean a List Card */}
          <Card
            elevation={3}
            sx={{
              minHeight: { xs: '400px', md: '420px', lg: '400px' },
              height: 'auto',
              borderRadius: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              display: 'flex',
              flexDirection: 'column',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(0, 149, 255, 0.3)'
              }
            }}
          >
                  <CardContent sx={{ width: '100%', p: { xs: 2.5, md: 3 }, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    {/* Top Content (flexible - grows to push bottom content down) */}
                    <Box sx={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {/* Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                        <ListCleanerIcon sx={{ fontSize: 32, color: '#0095FF', mr: 1.5 }} />
                        <Typography
                          variant="h6"
                          sx={{
                            textTransform: 'uppercase',
                            letterSpacing: '0.15em',
                            fontWeight: 500,
                            fontSize: '0.875rem'
                          }}
                        >
                          CLEAN A LIST
                        </Typography>
                      </Box>

                      {/* Description */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          lineHeight: 1.6,
                          fontSize: '0.875rem'
                        }}
                      >
                        Qualify prospects using data already in your CSV export
                      </Typography>

                      {/* What It Checks */}
                      <Box sx={{ textAlign: 'left', width: '100%', mb: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            mb: 1,
                            display: 'block'
                          }}
                        >
                          What it checks:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5, listStyleType: 'disc' }}>
                          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                            Job titles and seniority levels
                          </Typography>
                          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                            Company size and industry
                          </Typography>
                          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                            Geographic location
                          </Typography>
                          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                            Department and function
                          </Typography>
                          <Typography component="li" variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            Any existing CSV fields
                          </Typography>
                        </Box>
                      </Box>

                      {/* Best For */}
                      <Box sx={{ textAlign: 'left', width: '100%', mb: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            mb: 1,
                            display: 'block'
                          }}
                        >
                          Best for:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5, listStyleType: 'disc' }}>
                          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                            Filtering bad LinkedIn Sales Navigator exports
                          </Typography>
                          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                            Removing irrelevant job titles/industries quickly
                          </Typography>
                          <Typography component="li" variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            Basic firmographic qualification
                          </Typography>
                        </Box>
                      </Box>

                    </Box>

                    {/* Bottom Content (pinned at fixed distance from bottom) */}
                    <Box sx={{ width: '100%', mt: 3 }}>
                      {/* Pricing - moved closer to button */}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: '#0095FF',
                          fontSize: '0.875rem',
                          textAlign: 'center',
                          mb: 2
                        }}
                      >
                        1 credit per lead
                      </Typography>
                      {/* Button */}
                      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Button
                          variant="contained"
                          endIcon={<ArrowForwardIcon />}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 700
                          }}
                          onClick={() => handleNavigate('filter')}
                        >
                          Start Now
                        </Button>
                      </Box>

                      {/* Help Link */}
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.75rem'
                        }}
                      >
                        NEED HELP?{' '}
                        <Typography
                          component="a"
                          href="https://intercom.help/wyspur-ai/en/articles/12634207-the-list-cleaner-a-step-by-step-guide"
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="caption"
                          sx={{
                            color: '#0095FF',
                            fontSize: '0.75rem',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            '&:hover': {
                              color: '#0080E0'
                            }
                          }}
                        >
                          Read our step-by-step guide.
                        </Typography>
                      </Typography>
                    </Box>
                  </CardContent>
          </Card>

          {/* Analyze Websites Card */}
          <Card
            elevation={3}
            sx={{
              minHeight: { xs: '400px', md: '420px', lg: '400px' },
              height: 'auto',
              borderRadius: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              display: 'flex',
              flexDirection: 'column',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(255, 170, 0, 0.3)'
              }
            }}
          >
                  <CardContent sx={{ width: '100%', p: { xs: 2.5, md: 3 }, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    {/* Top Content (flexible - grows to push bottom content down) */}
                    <Box sx={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {/* Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                        <ResearchIcon sx={{ fontSize: 32, color: '#ffaa00', mr: 1.5 }} />
                        <Typography
                          variant="h6"
                          sx={{
                            textTransform: 'uppercase',
                            letterSpacing: '0.15em',
                            fontWeight: 500,
                            fontSize: '0.875rem'
                          }}
                        >
                          ANALYZE WEBSITES
                        </Typography>
                      </Box>

                      {/* Description */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          lineHeight: 1.6,
                          fontSize: '0.875rem'
                        }}
                      >
                        Research companies online to verify details beyond your CSV
                      </Typography>

                      {/* What It Checks */}
                      <Box sx={{ textAlign: 'left', width: '100%', mb: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            mb: 1,
                            display: 'block'
                          }}
                        >
                          What it checks:
                        </Typography>

                        {/* Website Only Mode */}
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            mb: 0.5,
                            display: 'block',
                            color: 'text.primary'
                          }}
                        >
                          Website Only Mode:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, mb: 1.5, pl: 2.5, listStyleType: 'disc' }}>
                          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                            Business model (SaaS, agency, ecommerce)
                          </Typography>
                          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                            Products/services offered
                          </Typography>
                          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                            Industries they serve
                          </Typography>
                          <Typography component="li" variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            B2B vs B2C focus
                          </Typography>
                        </Box>

                        {/* Enhanced Web Search */}
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            mb: 0.5,
                            display: 'block',
                            color: 'text.primary'
                          }}
                        >
                          Enhanced Web Search:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5, listStyleType: 'disc' }}>
                          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                            Funding status and amounts
                          </Typography>
                          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                            Employee count (LinkedIn)
                          </Typography>
                          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                            Social media presence
                          </Typography>
                          <Typography component="li" variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            Recent news and growth signals
                          </Typography>
                        </Box>
                      </Box>

                      {/* Best For */}
                      <Box sx={{ textAlign: 'left', width: '100%', mb: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            mb: 1,
                            display: 'block'
                          }}
                        >
                          Best for:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5, listStyleType: 'disc' }}>
                          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                            Verifying "Are they actually a SaaS company?"
                          </Typography>
                          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                            Finding funded companies (Series A+)
                          </Typography>
                          <Typography component="li" variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            Confirming specific business types
                          </Typography>
                        </Box>
                      </Box>

                    </Box>

                    {/* Bottom Content (pinned at fixed distance from bottom) */}
                    <Box sx={{ width: '100%', mt: 3 }}>
                      {/* Pricing - moved closer to button with two modes */}
                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: '#ffaa00',
                            fontSize: '0.875rem',
                            lineHeight: 1.6
                          }}
                        >
                          Website Only: 1 credit per lead
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: '#ffaa00',
                            fontSize: '0.875rem',
                            lineHeight: 1.6
                          }}
                        >
                          Enhanced Web Search: 5 credits per lead
                        </Typography>
                      </Box>
                      {/* Button */}
                      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Button
                          variant="contained"
                          color="secondary"
                          endIcon={<ArrowForwardIcon />}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 700,
                            backgroundColor: '#ffaa00',
                            '&:hover': {
                              backgroundColor: '#e69900'
                            }
                          }}
                          onClick={() => handleNavigate('research')}
                        >
                          Start Now
                        </Button>
                      </Box>

                      {/* Help Link */}
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.75rem'
                        }}
                      >
                        NEED HELP?{' '}
                        <Typography
                          component="a"
                          href="https://intercom.help/wyspur-ai/en/articles/12885378-the-website-analyzer-a-step-by-step-guide"
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="caption"
                          sx={{
                            color: '#ffaa00',
                            fontSize: '0.75rem',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            '&:hover': {
                              color: '#e69900'
                            }
                          }}
                        >
                          Read our step-by-step guide.
                        </Typography>
                      </Typography>
                    </Box>
                  </CardContent>
          </Card>

          {/* Empty Box 4 - Background Matching */}
          <Box sx={{ backgroundColor: 'transparent' }} />
        </Box>
      </Stack>
    </Container>
  );
}
